import { styled } from 'styled-components';
import { Icon, tk, Typography, SpaceBetween, Chip } from '@sunwebgroup/ui-components';

import { useAlternativeFilter } from '@/domain/prices/prices/hooks';

import Layout from '../Layout';
import { useIntl } from '@/intl';
import { useMonthFilter, useMultiSelectFilter, usePriceFilters, useSelectFilter } from '@/domain/prices/filters/hooks';
import type { MultiselectFilterType, SelectFilterType } from '@/domain/prices/filters/types';

const WarningIcon = styled(Icon)`
  color: ${tk('color_semantic_bg_warning')};
`;

const Wrapper = styled(SpaceBetween)`
  display: flex;
  flex-direction: column;
`;

const BoldChip = styled(Chip)`
  cursor: pointer;
  ${Typography} {
    font-weight: 600;
  }
`;

export function AlternativeFilters() {
  const intl = useIntl();
  const { resetSelection } = usePriceFilters();

  return (
    <Layout.Root>
      <WarningIcon glyph="IconWarningTriangle" size="lg" />
      <Layout.Content>
        <SpaceBetween direction="y" spacing={tk('spacing_md')}>
          <Layout.TitleDescription>
            <Typography $variant="body-lg-bold">
              {intl.formatMessage({ id: 'prices_alternative_filters_title' })}
            </Typography>
            <Typography $variant="body-md">
              {intl.formatMessage({ id: 'prices_alternative_filters_description' })}
            </Typography>
          </Layout.TitleDescription>
          <MonthAlternativeFilters />
          <MultipleAlternativeFilter filterType="Duration" />
          <MultipleAlternativeFilter filterType="DepartureAirport" />
          <MultipleAlternativeFilter filterType="RoomType" />
          <SingleAlternativeFilter filterType="Mealplan" />
          <SingleAlternativeFilter filterType="TransportType" />
          <Wrapper direction="y" spacing={tk('spacing_2xs')}>
            <Typography>{intl.formatMessage({ id: 'prices_alternative_filters_reset_filters_title' })}</Typography>
            <div>
              <BoldChip onClick={() => resetSelection()}>
                {intl.formatMessage({ id: 'prices_alternative_filters_reset_filters_description' })}
              </BoldChip>
            </div>
          </Wrapper>
        </SpaceBetween>
      </Layout.Content>
    </Layout.Root>
  );
}

const ChipsWrapper = styled(SpaceBetween)`
  display: flex;
  flex-wrap: wrap;
`;

const MonthAlternativeFilters = () => {
  const data = useAlternativeFilter('Month');
  const { setSelection } = useMonthFilter();

  if (!data) return null;

  return (
    <Wrapper direction="y" spacing={tk('spacing_2xs')}>
      <Typography>{data.title}</Typography>
      <ChipsWrapper direction="x" spacing={tk('spacing_sm')}>
        {data.options.map((option) => (
          <BoldChip key={option.value} value={option.value} onClick={() => setSelection(option.value)}>
            {option.text}
          </BoldChip>
        ))}
      </ChipsWrapper>
    </Wrapper>
  );
};

const MultipleAlternativeFilter = ({ filterType }: { filterType: MultiselectFilterType }) => {
  const data = useAlternativeFilter(filterType);
  const { setSelection } = useMultiSelectFilter(filterType);

  if (!data) return null;

  return (
    <Wrapper direction="y" spacing={tk('spacing_2xs')}>
      <Typography>{data.title}</Typography>
      <ChipsWrapper direction="x" spacing={tk('spacing_sm')}>
        {data.options.map((option) => (
          <BoldChip key={option.value} value={option.value} onClick={() => setSelection([option.value])}>
            {option.text}
          </BoldChip>
        ))}
      </ChipsWrapper>
    </Wrapper>
  );
};

const SingleAlternativeFilter = ({ filterType }: { filterType: SelectFilterType }) => {
  const data = useAlternativeFilter(filterType);
  const { setSelection } = useSelectFilter(filterType);

  if (!data) return null;

  return (
    <Wrapper direction="y" spacing={tk('spacing_2xs')}>
      <Typography>{data.title}</Typography>
      <ChipsWrapper direction="x" spacing={tk('spacing_sm')}>
        {data.options.map((option) => (
          <BoldChip key={option.value} value={option.value} onClick={() => setSelection([option.value])}>
            {option.text}
          </BoldChip>
        ))}
      </ChipsWrapper>
    </Wrapper>
  );
};
